import React, { useEffect, useState } from 'react';
import { MdOutlineRefresh, MdOutlineVpnKey } from 'react-icons/md';
import { AiOutlineCheck } from 'react-icons/ai';
import { FiAlignLeft } from 'react-icons/fi';
import { TbAlignLeft } from 'react-icons/tb';
import { VscSearch } from 'react-icons/vsc';

import Get from '../api/internal/Get';
import Create from '../helpers/panel/Create';
import Cookies from 'js-cookie';
import {
  GenerateQueryString,
  ParseUrlForSearchParams,
} from '../helpers/search/HelperFunctions';
import FilterData from '../helpers/filter/FilterData';
import DateOnlyFormatter from '../helpers/inputs/DateOnlyFormatter';
import Header from '../layouts/Header';
import Filter from '../helpers/filter/Filter';
import BBITable from '../helpers/bBITable/BBITable';
import Input from '../assets/essentials/Input';
import Button from '../assets/essentials/Button';
import View from '../helpers/slab/View';
import ViewRental from '../features/views/rental/Rental';
import ViewVendor from '../features/views/vendor/Vendor';
import ViewCustomer from '../features/views/customer/Customer';
import CreateRental from '../features/creates/rental/Rental';
import classesGlobal from '../assets/Global.module.css';
import classesTable from '../helpers/bBITable/BBITable.module.css';
import classesCustom from '../helpers/search/CustomSearch.module.css';
import Select from '../assets/essentials/Select';
import OptionList from '../helpers/options/OptionList';
import DownloadCsvButton from '../helpers/buttons/DownloadCsvButton';
import { PageLoadingSpinner } from '../helpers/loadingSpinner/LoadingSpinner';

function Rental({ toggleBoardRefresh }) {
  const [filterValue, setFilterValue] = useState('');
  const [rentalBoard, setRentalBoard] = useState([]);
  const [isCompact, setIsCompact] = useState(
    Cookies.get('rentalIsCompact') === 'true',
  );
  const [showViewOption, setShowViewOption] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const defaultSearchParams = {
    rentalId: null,
    trailerNumber: null,
    assetName: null,
    reference: null,
    customerName: null,
    rentalStatusId: 0,
    startDateMin: null,
    startDateMax: null,
    endDateMin: null,
    endDateMax: null,
  };
  const [rentalSearchParams, setRentalSearchParams] =
    useState(defaultSearchParams);
  const [rentalStatusOptions, setRentalStatusOptions] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const { rentalId, reference, customerName, rentalStatusId } =
      ParseUrlForSearchParams(defaultSearchParams);
    if (rentalStatusOptions.length === 0) {
      GetRentalStatusOptions();
    }
    if (rentalId != null || reference != null || customerName != null) {
      setIsSearchOpen(true); //open search form if using search params from URL
      setRentalSearchParams({
        rentalId: rentalId,
        trailerNumber: null,
        reference: reference,
        customerName: customerName,
        rentalStatusId: rentalStatusId,
        assetName: null,
        startDateMin: null,
        startDateMax: null,
        endDateMin: null,
        endDateMax: null,
      });
      GetRentalBoard({
        rentalId: rentalId,
        reference: reference,
        customerName: customerName,
        rentalStatusId: rentalStatusId,
      });
    } else {
      GetRentalBoard(defaultSearchParams);
    }
  }, [toggleBoardRefresh]);

  function GetRentalStatusOptions() {
    Get(`/Rental/GetRentalStatusOptions/`).then(response => {
      if (response) {
        setRentalStatusOptions(response?.data);
      }
    });
  }

  function GetRentalBoard(searchParamsFromForm?: Object) {
    setIsLoading(true);
    const searchParams = searchParamsFromForm
      ? searchParamsFromForm
      : rentalSearchParams;
    const queryString = '?' + GenerateQueryString(searchParams);
    const newUrl =
      window.location.origin + window.location.pathname + queryString;
    window.history.replaceState(null, null, newUrl);
    Get(`/Rental/GetRentalBoard${queryString}`)
      .then(response => {
        if (response) {
          setRentalBoard(FormatRentals(response.data));
          setIsLoading(false);
        }
      })
      .finally(() => setIsLoading(false));
  }

  function SearchRentals(e) {
    e.preventDefault();
    setFilterValue('');
    const formData = {
      rentalId: e.target.rentalId?.value,
      trailerNumber: e.target.trailerNumber?.value,
      reference: e.target.reference?.value,
      customerName: e.target.customerName?.value,
      rentalStatusId: e.target.rentalStatusId?.value,
      assetName: e.target.assetName?.value,
      startDateMin: e.target.startDateMin?.value,
      startDateMax: e.target.startDateMax?.value,
      endDateMin: e.target.endDateMin?.value,
      endDateMax: e.target.endDateMax?.value,
    };
    setRentalSearchParams(formData);
    GetRentalBoard(formData);
  }

  function ClearSearchParams() {
    setRentalSearchParams(defaultSearchParams);
  }

  function RefreshRentalBoard() {
    setIsSearchOpen(false);
    GetRentalBoard(defaultSearchParams);
  }

  function ToggleOpenSearch() {
    if (!isSearchOpen) {
      setRentalBoard([]);
    } else {
      ClearSearchParams();
      GetRentalBoard(defaultSearchParams);
    }
    setIsSearchOpen(!isSearchOpen);
    setFilterValue('');
  }

  function FormatRentals(rentals) {
    return rentals.map(rental => ({
      ...rental,
      startDate: DateOnlyFormatter(rental.startDate),
      endDate: DateOnlyFormatter(rental.endDate),
      totalInvoice: rental.totalInvoice.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      totalBill: rental.totalBill.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      vendorName: rental.vendors?.map(vendor => vendor.vendorName).join(', '),
    }));
  }

  // passed into BBI Table to build table headers and rows
  let columnConfig = [
    {
      key: '1',
      attribute: 'rentalId',
      header: 'Rental #',
      onClick: e => View(<ViewRental rentalId={e.target.innerText} />),
      dataType: 'number',
    },
    {
      key: '2',
      attribute: 'rentalStatus',
      header: 'Status',
      attributeprimaryhidden: 'rentalId',
      onClick: e =>
        View(<ViewRental rentalId={e.target.dataset.attributeprimaryhidden} />),
      width: '7.5rem',
    },
    {
      key: '3',
      attribute: 'customerName',
      attributeprimaryhidden: 'customerId',
      header: 'Customer',
      onClick: e =>
        View(
          <ViewCustomer
            customerName={e.target.innerText}
            customerId={e.target.dataset.attributeprimaryhidden}
          />,
        ),
    },
    {
      key: '4',
      attribute: 'reference',
      header: 'Reference',
      // Data contains loadId, rentalId resulted in a Not Found error.
      // Will likely need updated in the future.
      attributeprimaryhidden: 'rentalId',
      onClick: e =>
        View(<ViewRental rentalId={e.target.dataset.attributeprimaryhidden} />),
    },
    {
      key: '5',
      attribute: 'trailerNumber',
      header: 'Trailer #',
      dataType: 'string',
    },
    {
      key: '6',
      attribute: 'startDate',
      header: 'Start Date',
      dataType: 'date',
    },
    {
      key: '7',
      attribute: 'endDate',
      header: 'End Date',
      dataType: 'date',
    },
    {
      key: '8',
      attribute: 'vendorName',
      attributeprimaryhidden: 'vendorId',
      header: 'Vendor',
      onClick: e =>
        View(
          <ViewVendor
            vendorName={e.target.innerText.split(', ')[0]}
            vendorId={e.target.dataset.attributeprimaryhidden}
          />,
        ),
    },
    {
      key: '9',
      attribute: 'totalInvoice',
      header: 'Total Invoice',
      dataType: 'currency',
    },
    {
      key: '10',
      attribute: 'totalBill',
      header: 'Total Bill',
      dataType: 'currency',
    },
  ];

  const RentalSearchDisplay = ({ rentals, filterValue, columnConfig }) => (
    <>
      <div className={classesTable.title}>Results</div>
      {rentals.length > 0 ? (
        <div className={classesTable.normalView}>
          <BBITable
            data={FilterData(rentals, filterValue)}
            columns={columnConfig}
            compactView={false}
          />
        </div>
      ) : (
        <p>No Results.</p>
      )}
    </>
  );

  const RentalSearchForm = () => {
    return (
      <>
        <div className={classesCustom.customSearch}>
          <form onSubmit={e => SearchRentals(e)}>
            <label>
              Rental #
              <Input
                type="number"
                name="rentalId"
                defaultValue={rentalSearchParams.rentalId}
              />
            </label>
            <label>
              Reference
              <Input
                type="text"
                name="reference"
                defaultValue={rentalSearchParams.reference}
              />
            </label>
            <label>
              Customer Name
              <Input
                type="text"
                name="customerName"
                defaultValue={rentalSearchParams.customerName}
              />
            </label>
            <label>
              Trailer #
              <Input
                type="text"
                name="trailerNumber"
                defaultValue={rentalSearchParams.trailerNumber}
              />
            </label>
            <label>
              Asset Name
              <Input
                type="text"
                name="assetName"
                defaultValue={rentalSearchParams.assetName}
              />
            </label>
            <div className={classesCustom.dateRange}>
              <span className={classesGlobal.span2}>Rental Start Date</span>
              <label htmlFor="startDateMin">Start: </label>
              <Input
                id="startDateMin"
                type="date"
                name="startDateMin"
                defaultValue={rentalSearchParams.startDateMin}
              />
              <label htmlFor="startDateMax">End: </label>
              <Input
                id="startDateMax"
                type="date"
                name="startDateMax"
                defaultValue={rentalSearchParams.startDateMax}
              />
            </div>
            <div className={classesCustom.dateRange}>
              <span className={classesGlobal.span2}>Rental End Date</span>
              <label htmlFor="endDateMin">Start: </label>
              <Input
                id="endDateMin"
                type="date"
                name="endDateMin"
                defaultValue={rentalSearchParams.endDateMin}
              />
              <label htmlFor="endDateMax">End: </label>
              <Input
                id="endDateMax"
                type="date"
                name="endDateMax"
                defaultValue={rentalSearchParams.endDateMax}
              />
            </div>
            <label>
              Rental Status
              <Select
                name="rentalStatusId"
                defaultValue={rentalSearchParams.rentalStatusId}
              >
                <option value="0">Active Rentals</option>
                <OptionList
                  optionData={rentalStatusOptions}
                  attributeID="rentalStatusId"
                  attributeName="status"
                />
                <option value="-1">Any Status</option>
              </Select>
            </label>
            <div className={classesCustom.submitHolder}>
              <Button type="submit" variant="good">
                Search
              </Button>
              <Button variant="bad" onClick={ClearSearchParams}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
        <div>
          <RentalSearchDisplay
            rentals={rentalBoard}
            filterValue={filterValue}
            columnConfig={columnConfig}
          />
        </div>
      </>
    );
  };

  const CompactViewButton = () => {
    return (
      <div className={classesGlobal.dropdown}>
        <button
          aria-label="Open Toggle Compact Menu"
          onClick={() => setShowViewOption(!showViewOption)}
        >
          {isCompact ? <FiAlignLeft /> : <TbAlignLeft />}
        </button>
        {showViewOption && (
          <div className={classesGlobal.dropdownOptions}>
            <div
              onClick={() => {
                setIsCompact(false);
                Cookies.set('rentalIsCompact', 'false');
                setShowViewOption(false);
              }}
            >
              {!isCompact ? <AiOutlineCheck /> : <div />}
              <TbAlignLeft />
              <span>Normal View</span>
            </div>
            <div
              onClick={() => {
                setIsCompact(true);
                Cookies.set('rentalIsCompact', 'true');
                setShowViewOption(false);
              }}
            >
              {isCompact ? <AiOutlineCheck /> : <div />}
              <FiAlignLeft />
              <span>Compact View</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <h1>Rental</h1>
      <Header>
        <div>
          <button
            onClick={() =>
              Create(<CreateRental RefreshBoard={GetRentalBoard} />)
            }
          >
            <MdOutlineVpnKey />
            <span>Create a Rental</span>
          </button>
          <button onClick={RefreshRentalBoard}>
            <MdOutlineRefresh />
            <span>Refresh Board</span>
          </button>
          <button onClick={() => ToggleOpenSearch()}>
            <VscSearch />
            <span>Search Rental</span>
          </button>
          <DownloadCsvButton
            data={FilterData(rentalBoard, filterValue)}
            columnConfig={columnConfig}
            fileName={`Rentals_${new Date().toLocaleDateString()}.csv`}
          />
        </div>
        <div>
          <CompactViewButton />
          <Filter
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            placeholder="Filter rentals"
          />
        </div>
      </Header>
      {isLoading ? (
        <PageLoadingSpinner />
      ) : isSearchOpen ? (
        <RentalSearchForm />
      ) : rentalBoard.length > 0 ? (
        <BBITable
          data={FilterData(rentalBoard, filterValue)}
          columns={columnConfig}
          compactView={isCompact}
        />
      ) : (
        <p>No rentals.</p>
      )}
    </>
  );
}

export default Rental;
